import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AUTOMATED_CAMPAIGNS_LINK_URL,
  PERFORMANCE_DASHBOARD_LINK_URL,
} from '~/modules/link-farm/components/LinkList/constants';
import { LinkPage } from '~/modules/link-farm/LinkPage';
import type { InternalLinkType } from '~/modules/link-farm/types';
import { Mc3Client } from '~/modules/marketing/pages/automated-creator-campaign/api/client';

import { SignInReminderModal } from '../../components/SignInReminderModal/SignInReminderModal';
import { AboutAutomatedCreatorCampaignsModal } from '../automated-creator-campaign/components/AboutAutomatedCreatorCampaignsModal/AboutAutomatedCreatorCampaignsModal';
import { AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY } from '../automated-creator-campaign/constants';
import type { GetCampaignResponse } from '../automated-creator-campaign/types';

const NEW_CAMPAIGN_PATH = '/marketing/automated-creator-campaigns/new';

export function MarketingPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showAboutCreatorModal, setShowAboutCreatorModal] = useState(false);

  const navigateByCampaignCount = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams();

      searchParams.append('limit', '1');

      const data = await Mc3Client.get('api/campaigns', {
        searchParams,
      }).json<GetCampaignResponse>();

      if (data.total_count > 0) {
        navigate(AUTOMATED_CAMPAIGNS_LINK_URL);
      } else {
        navigate(NEW_CAMPAIGN_PATH);
      }
    } catch {
      console.error('Failed to fetch campaigns');
    }
  }, [navigate]);

  const handleAboutCreatorModalClose = useCallback(() => {
    setShowAboutCreatorModal(false);
  }, []);

  const handleAboutCreatorModalConfirm = useCallback(
    ({ dontShowAgain }: { dontShowAgain: boolean }) => {
      if (dontShowAgain) {
        localStorage.setItem(
          AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY,
          'true',
        );
      }

      setShowAboutCreatorModal(false);
      void navigateByCampaignCount();
    },
    [navigateByCampaignCount],
  );

  const handleInternalLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    type?: InternalLinkType,
  ) => {
    if (type === 'automatedCreatorCampaigns') {
      event.preventDefault();
      event.stopPropagation();

      if (
        localStorage.getItem(
          AUTOMATED_CREATOR_CAMPAIGNS_INFO_MODAL_DISMISSED_KEY,
        ) !== 'true'
      ) {
        setShowAboutCreatorModal(true);

        return;
      }

      void navigateByCampaignCount();

      return;
    }

    // Need to show a modal about possibly signing in to Databricks but only
    // for the performance dashboard.
    if (type === 'performanceDashboard') {
      const signInReminderModalViewCount = localStorage.getItem(
        'signInReminderModalViewCount',
      );

      const currentCount = signInReminderModalViewCount
        ? parseInt(signInReminderModalViewCount, 10)
        : 0;

      if (currentCount < 2) {
        event.preventDefault();
        event.stopPropagation();
        localStorage.setItem(
          'signInReminderModalViewCount',
          (currentCount + 1).toString(),
        );

        setShowModal(true);
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <LinkPage
        category="marketing"
        onInternalLinkClick={handleInternalLinkClick}
      />
      <SignInReminderModal
        isOpen={showModal}
        onClose={handleModalClose}
        url={PERFORMANCE_DASHBOARD_LINK_URL}
      />
      <AboutAutomatedCreatorCampaignsModal
        isOpen={showAboutCreatorModal}
        onClose={handleAboutCreatorModalClose}
        onConfirm={handleAboutCreatorModalConfirm}
      />
    </>
  );
}

MarketingPage.displayName = 'MarketingPage';
